import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useCallback,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import {
  FileSpreadsheet,
  Upload,
  Download,
  X,
  CheckCircle2,
  Trash2,
  AlertCircle,
  AlertTriangle,
  RotateCcw,
  Check,
} from "lucide-react";
import shared from "./Shared";
import ApiClient from "../../methods/api/apiClient";
import methodModel from "../../methods/methods";
import Layout from "../../components/global/layout";
import SelectDropdown from "../../components/common/SelectDropdown";
import loader from "../../methods/loader";
import swal from "sweetalert";
import environment from "../../environment";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  companyName: "",
  mobileNo: "",
  address: "",
  image: "",
  state: "",
  country: "",
  pinCode: "",
};

// ─── FormInput ────────────────────────────────────────────────────
const FormInput = ({
  label,
  name,
  type = "text",
  required,
  textarea,
  value,
  onChange,
  error,
  placeholder,
}) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all resize-none ${error ? "border-red-400" : "border-gray-200"}`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${error ? "border-red-400" : "border-gray-200"}`}
      />
    )}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

// ─── PhoneField ───────────────────────────────────────────────────
const PhoneField = ({ label, required, value, onChange, error }) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <PhoneInput
      required={required}
      enableSearch={true}
      country="us"
      autoFormat={false}
      placeholder="Enter phone number"
      value={value}
      countryCodeEditable={true}
      onChange={onChange}
      buttonClass="!border !border-gray-200 !rounded-l-xl !bg-transparent"
      inputClass={`!h-10 !w-full !border !rounded-xl !text-sm !text-gray-800 !pl-12 focus:!ring-2 focus:!ring-[#115B9D]/30 focus:!border-[#115B9D] !transition-all ${error ? "!border-red-400" : "!border-gray-200"
        }`}
    />
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

// ─── Avatar / Image Upload ────────────────────────────────────────
const AvatarUpload = ({ imagePreview, onChange }) => (
  <label className="cursor-pointer">
    <div
      className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${imagePreview ? "border-[#115B9D] bg-[#115B9D17]" : "border-gray-200 hover:border-[#115B9D] hover:bg-[#115B9D17]"}`}
    >
      {imagePreview ? (
        <div className="flex items-center gap-4">
          <img
            src={imagePreview}
            alt="Preview"
            className="size-16 rounded-xl object-cover border border-gray-200"
          />
          <div className="text-left">
            <p className="text-sm text-gray-700 font-medium">Image selected</p>
            <p className="text-xs text-gray-600 mt-0.5">Click to change</p>
            <p className="text-xs text-gray-600">Max 500KB · JPG, PNG, WEBP</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="size-10 rounded-full bg-[#115b9d29] flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#115B9D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" x2="12" y1="3" y2="15" />
            </svg>
          </div>
          <p className="text-sm text-[#115B9D]">Click to upload avatar</p>
          <p className="text-xs text-gray-600">
            Max 500KB · JPG, JPEG, PNG, WEBP
          </p>
        </div>
      )}
      <input
        type="file"
        className="hidden"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={onChange}
      />
    </div>
  </label>
);

// ─── User Avatar Cell ─────────────────────────────────────────────
const UserAvatar = ({ user }) => {
  const initials =
    `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
  return (
    <div className="flex items-center gap-3">
      <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center overflow-hidden shrink-0">
        {user.image ? (
          <img
            src={methodModel.userImg(user.image)}
            alt={`${user.firstName} ${user.lastName}`}
            className="size-full object-cover rounded-xl"
          />
        ) : (
          <span className="text-sm capitalize font-semibold text-[#115B9D]">
            {initials || "U"}
          </span>
        )}
      </div>
      <div>
        <p className="text-sm capitalize font-medium text-gray-900 whitespace-nowrap">
          {user.firstName} {user.lastName}
        </p>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const UsersListing = () => {
  const [formPage, setFormPage] = useState(false);
  const [users, setUsers] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [filters, setFilter] = useState({
    page: 1,
    count: 10,
    search: "",
    status: "",
  });

  // ─── Import State ────────────────────────────────────────────────
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [resultTab, setResultTab] = useState("skipped"); // "skipped" | "imported"
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchUsers = async (p = {}) => {
    loader(true);
    const params = {
      page: p.page ?? filters.page,
      count: p.count ?? filters.count,
      search: p.search ?? searchQuery,
      status: p.status ?? filters.status,
      role: environment.user_role_id,
    };
    const query = new URLSearchParams(params).toString();
    await ApiClient.get(`user/listing?${query}`)
      .then((res) => {
        if (res?.success) {
          setUsers(res?.data?.data || []);
          setTotal(res.data.total || 0);
        }
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => fetchUsers({ search: searchQuery }), 500);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      swal({
        text: "Image size must be less than 500KB",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      swal({
        text: "Please upload a valid image (JPG, JPEG, PNG, WEBP)",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    setImagePreview(URL.createObjectURL(file));
    loader(true);
    ApiClient.postFormData("upload/image", { file }).then((res) => {
      if (res.fileName) setForm((prev) => ({ ...prev, image: res.fileName }));
      loader(false);
    });
  };

  const validate = () => {
    const errors = {};
    if (!form.firstName?.trim()) errors.firstName = "First name is required";
    if (!form.lastName?.trim()) errors.lastName = "Last name is required";

    // Email validation
    if (!form.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    // Mobile validation
    if (form.mobileNo && form.mobileNo.replace(/\D/g, "").length < 7) {
      errors.mobileNo = "Please enter a valid mobile number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setImagePreview(null);
    setFormErrors({});
    setIsEditMode(false);
    setEditingId(null);
    setFormPage(true);
  };

  const openEditModal = async (id) => {
    loader(true);
    await ApiClient.get(`user/profile?id=${id}`)
      .then((res) => {
        if (res?.success) {
          const d = res.user_data;
          setForm({
            firstName: d.firstName || "",
            lastName: d.lastName || "",
            email: d.email || "",
            companyName: d.companyName || "",
            mobileNo: d.mobileNo || "",
            address: d.address || "",
            image: d.image || "",
            state: d.state || "",
            country: d.country || "",
            pinCode: d.pinCode || "",
          });
          setImagePreview(d.image ? methodModel.userImg(d.image) : null);
          setFormErrors({});
          setIsEditMode(true);
          setEditingId(id);
          setFormPage(true);
        }
      })
      .catch(console.error);
    loader(false);
  };

  const closeModal = () => {
    setFormPage(false);
    setForm(emptyForm);
    setImagePreview(null);
    setFormErrors({});
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    loader(true);
    const payload = { ...form, role: environment.user_role_id };
    if (isEditMode) {
      delete payload.role;
      delete payload.email;
    }
    const endpoint = isEditMode ? "user/profile" : "user/add-user";
    await ApiClient[isEditMode ? "put" : "post"](
      endpoint,
      isEditMode ? { ...payload, id: editingId } : payload,
    )
      .then((res) => {
        if (res?.success) {
          swal({
            text: `User ${isEditMode ? "updated" : "added"} successfully.`,
            icon: "success",
            className: "smallModal",
          });
          closeModal();
          fetchUsers();
        }
      })
      .catch(console.error);
    loader(false);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This user will be permanently deleted.",
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      await ApiClient.delete(`user/delete?id=${id}`)
        .then(async (res) => {
          if (res?.success) {
            await fetchUsers();
            swal({
              text: "User deleted successfully.",
              icon: "success",
              className: "smallModal",
            });
          }
        })
        .catch(console.error);
      loader(false);
    });
  };

  const statusChange = (item, status) => {
    swal({
      title: "Are you sure?",
      text: `Do you want to ${status === "active" ? "activate" : "deactivate"} this User?`,
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Yes"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put(`user/profile`, {
          id: item._id || item.id,
          status,
        });
        if (res?.success) {
          await fetchUsers();
          swal({
            text: `User ${status === "active" ? "activated" : "deactivated"} successfully.`,
            icon: "success",
            className: "smallModal",
          });
        }
      } catch (err) {
        console.error(err);
      }
      loader(false);
    });
  };

  const changestatus = (e, key) => {
    setFilter((prev) => ({ ...prev, [key]: e, page: 1 }));
    fetchUsers({ [key]: e, page: 1 });
  };

  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil(total / filters.count);
    if (newPage < 1 || newPage > maxPage) return;
    setFilter((prev) => ({ ...prev, page: newPage }));
    fetchUsers({ page: newPage });
  };

  // ─── Import Handlers ─────────────────────────────────────────────
  const openImportModal = () => {
    setImportFile(null);
    setImportResult(null);
    setResultTab("skipped");
    setDragOver(false);
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (isUploading) return;
    setIsImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleResetImport = () => {
    setImportResult(null);
    setImportFile(null);
    setResultTab("skipped");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSampleDownload = async () => {
    setIsDownloadingSample(true);
    loader(true);
    try {
      const res = await ApiClient.downloadFile(
        "user/sample-excel",
        {},
        "sample-users.xlsx",
      );
      if (res?.success) {
        toast.success("Sample Excel template downloaded successfully.");
      }
    } catch (error) {
      console.error("Error downloading sample file:", error);
      toast.error(error?.message || "Failed to download sample file");
    } finally {
      setIsDownloadingSample(false);
      loader(false);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const validExtensions = [".xlsx", ".xls"];
    const isValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext),
    );

    if (!isValidExtension) {
      swal({
        text: "Please select a valid Excel file (.xlsx or .xls)",
        icon: "error",
        className: "smallModal",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      swal({
        text: "File size must be less than 10MB",
        icon: "error",
        className: "smallModal",
      });
      return;
    }

    setImportFile(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      swal({
        text: "Please select an Excel file to import.",
        icon: "warning",
        className: "smallModal",
      });
      return;
    }

    setIsUploading(true);
    loader(true);

    try {
      const res = await ApiClient.postFormFileData("user/import-excel", {
        file: importFile,
        role: environment.user_role_id,
      });

      if (res?.success) {
        await fetchUsers({ page: 1 });
        const resultData = res?.data;

        if (
          resultData &&
          (resultData.skippedCount > 0 ||
            (Array.isArray(resultData?.skipped) &&
              resultData.skipped.length > 0))
        ) {
          // Show rejection breakdown report inside modal
          setImportResult(resultData);
          setResultTab("skipped");
        } else {
          swal({
            title: "Import Successful!",
            text:
              res?.message ||
              `All ${resultData?.importedCount || resultData?.totalRows || ""} customers imported successfully.`,
            icon: "success",
            className: "smallModal",
          });
          closeImportModal();
        }
      } else {
        const errMsg =
          res?.error?.message ||
          res?.message ||
          "Failed to import customers. Please check the file format.";
        swal({
          title: "Import Failed",
          text: errMsg,
          icon: "error",
          className: "smallModal",
        });
      }
    } catch (err) {
      console.error("Import error:", err);
      swal({
        title: "Error",
        text: err?.message || "An unexpected error occurred during import.",
        icon: "error",
        className: "smallModal",
      });
    } finally {
      setIsUploading(false);
      loader(false);
    }
  };

  return (
    <Layout>
      {!formPage ? (
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          {/* ─── Page Header ─── */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex flex-col gap-1">
              <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
                Customers
              </h2>
              <p className="text-sm sm:text-md text-gray-600">
                {total} customer{total !== 1 ? "s" : ""} total
              </p>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={openImportModal}
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-xl transition-all font-medium px-4 py-2.5 shadow-sm hover:border-[#115B9D]/40 hover:text-[#115B9D]"
              >
                <FileSpreadsheet className="size-4 text-[#008236]" />
                Import Customers
              </button>
              <button
                onClick={openAddModal}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-90 text-white text-sm rounded-xl transition-all font-medium px-5 py-2.5 shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Customer
              </button>
            </div>
          </div>

          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
            {/* ─── Filters ─── */}
            <div className="flex justify-between flex-wrap gap-3 mb-4">
              <div className="relative w-full sm:w-[350px]">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <SelectDropdown
                  id="statusDropdown"
                  theme="search"
                  displayValue="name"
                  placeholder="All status"
                  intialValue={filters?.status}
                  result={(e) => changestatus(e.value, "status")}
                  isClearable={false}
                  crossIcon={filters?.status ? true : false}
                  options={shared.filterStatus}
                />
              </div>
            </div>

            {/* ─── Table ─── */}
            {/* ─── Table ─── */}

            {/* Desktop Table */}
            <div className="hidden md:block overflow-auto scrollbar border border-gray-200 rounded-xl">
              <table className="w-full min-w-[700px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {[
                      "Actions",
                      "Full Name",
                      "Email",
                      "Location",
                      "Status",
                    ].map((h) => (
                      <th key={h} className="text-left py-3 px-5">
                        <span className="text-sm text-gray-700 font-[400] whitespace-nowrap">
                          {h}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16">
                        <div className="flex flex-col items-center gap-3">
                          <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-gray-600"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-500">
                            No customers found
                          </p>
                          <button
                            onClick={openAddModal}
                            className="text-[#115B9D] text-sm hover:underline"
                          >
                            Add your first customer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users?.map((item, index) => (
                      <tr
                        key={item.id || item._id || index}
                        className="hover:bg-gray-50/60 transition-colors"
                      >
                        {/* Actions */}
                        <td className="py-3 px-5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(item.id || item._id)}
                              title="Edit"
                              className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id || item._id)}
                              title="Delete"
                              className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                <line x1="10" x2="10" y1="11" y2="17" />
                                <line x1="14" x2="14" y1="11" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </td>

                        {/* User */}
                        <td className="py-3 px-5">
                          <UserAvatar user={item} />
                        </td>

                        {/* Email */}
                        <td className="py-3 px-5">
                          <p className="text-sm text-gray-800 whitespace-nowrap">
                            {item.email || (
                              <span className="text-gray-600">—</span>
                            )}
                          </p>
                        </td>

                        {/* Location */}
                        <td className="py-3 px-5">
                          <p className="text-sm text-gray-800 whitespace-nowrap">
                            {[item.state, item.country]
                              .filter(Boolean)
                              .join(", ") || (
                                <span className="text-gray-600">—</span>
                              )}
                          </p>
                          {item.pinCode && (
                            <p className="text-xs text-gray-600">
                              {item.pinCode}
                            </p>
                          )}
                        </td>

                        {/* Status */}
                        <td className="py-3 px-5 cursor-pointer">
                          {item.status === "active" ? (
                            <span
                              onClick={() => statusChange(item, "deactive")}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-xs"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <path d="m9 12 2 2 4-4" />
                              </svg>
                              Active
                            </span>
                          ) : (
                            <span
                              onClick={() => statusChange(item, "active")}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-xs"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                              </svg>
                              Deactive
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden">
              {users?.length === 0 ? (
                <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                  <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-600"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">No customers found</p>
                  <button
                    onClick={openAddModal}
                    className="text-[#115B9D] text-sm hover:underline"
                  >
                    Add your first customer
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {users?.map((item, index) => (
                    <div
                      key={item.id || item._id || index}
                      className="border border-gray-200 rounded-xl p-4 bg-white"
                    >
                      {/* Top row: User + Actions */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <UserAvatar user={item} />
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => openEditModal(item.id || item._id)}
                            title="Edit"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id || item._id)}
                            title="Delete"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              <line x1="10" x2="10" y1="11" y2="17" />
                              <line x1="14" x2="14" y1="11" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Email</span>
                        <p className="text-sm text-gray-800 text-right break-all">
                          {item.email || (
                            <span className="text-gray-600">—</span>
                          )}
                        </p>
                      </div>

                      {/* Location */}
                      <div className="flex justify-between items-start py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Location</span>
                        <div className="text-right">
                          <p className="text-sm text-gray-800">
                            {[item.state, item.country]
                              .filter(Boolean)
                              .join(", ") || (
                                <span className="text-gray-600">—</span>
                              )}
                          </p>
                          {item.pinCode && (
                            <p className="text-xs text-gray-600">
                              {item.pinCode}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Status</span>
                        {item.status === "active" ? (
                          <span
                            onClick={() => statusChange(item, "deactive")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-xs cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="m9 12 2 2 4-4" />
                            </svg>
                            Active
                          </span>
                        ) : (
                          <span
                            onClick={() => statusChange(item, "active")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-xs cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            Deactive
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Pagination ─── */}
            {total > filters.count && (
              <div className="flex flex-wrap gap-y-2 gap-x-5 items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {(filters.page - 1) * filters.count + 1} to{" "}
                  {Math.min(filters.page * filters.count, total)} of {total}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handlePageChange(filters.page - 1)}
                    disabled={filters.page === 1}
                    className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                  >
                    Prev
                  </button>
                  {Array.from(
                    { length: Math.ceil(total / filters.count) },
                    (_, i) => i + 1,
                  ).map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${Number(filters.page) === Number(p) ? "bg-[#115B9D] text-white" : "border"}`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(filters.page + 1)}
                    disabled={filters.page === Math.ceil(total / filters.count)}
                    className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="">
          <div className="">
            {/* ─── Page Header ─── */}
            <div className="flex items-center gap-3 mb-8">
              <div className="size-10 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#115B9D"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-[600] text-gray-800 tracking-tight">
                  {isEditMode ? "Edit Customer" : "Add New Customer"}
                </h1>
                <p className="text-sm text-gray-600 mt-0.5">
                  {isEditMode
                    ? "Update the customer details below"
                    : "Fill in the details to add a new customer"}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* ── Avatar Upload ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-4">
                  Profile Photo
                </p>
                <AvatarUpload
                  imagePreview={imagePreview}
                  onChange={handleImageChange}
                />
              </div>

              {/* ── Personal Info ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-5">
                  Personal Info
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid grid-cols-2 gap-4 sm:contents">
                  <FormInput
                    label="First Name"
                    name="firstName"
                    required
                    placeholder="John"
                    value={form.firstName}
                    onChange={handleChange}
                    error={formErrors.firstName}
                  />
                  <FormInput
                    label="Last Name"
                    name="lastName"
                    required
                    placeholder="Doe"
                    value={form.lastName}
                    onChange={handleChange}
                    error={formErrors.lastName}
                  />
                  </div>
                  <FormInput
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    error={formErrors.email}
                    disabled={isEditMode}
                  />
                  <PhoneField
                    label="Mobile Number"
                    // required
                    value={form.mobileNo}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, mobileNo: e }));
                      if (formErrors.mobileNo)
                        setFormErrors((prev) => ({ ...prev, mobileNo: "" }));
                    }}
                    error={formErrors.mobileNo}
                  />
                </div>
              </div>

              {/* ── Address ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-5">
                  Address
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Street Address */}
                  <div className="sm:col-span-2">
                    <FormInput
                      label="Street Address"
                      name="address"
                      textarea
                      placeholder="123 Main St, Apt 4B"
                      value={form.address}
                      onChange={handleChange}
                      error={formErrors.address}
                    />
                  </div>

                  {/* State */}
                  <FormInput
                    label="State / Province"
                    name="state"
                    placeholder="California"
                    value={form.state}
                    onChange={handleChange}
                    error={formErrors.state}
                  />

                  {/* Country + Pin */}
                  <div className="grid grid-cols-2 gap-4 sm:contents">
                    <FormInput
                      label="Country"
                      name="country"
                      placeholder="United States"
                      value={form.country}
                      onChange={handleChange}
                      error={formErrors.country}
                    />

                    <FormInput
                      label="Pin / ZIP Code"
                      name="pinCode"
                      placeholder="90210"
                      value={form.pinCode}
                      onChange={handleChange}
                      error={formErrors.pinCode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Action Buttons ── */}
            <div className="flex gap-2 flex-wrap items-center justify-end mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {isEditMode ? (
                    <>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </>
                  ) : (
                    <path d="M12 5v14M5 12h14" />
                  )}
                </svg>
                {isEditMode ? "Update Customer" : "Add Customer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Import Customers Modal ─── */}
      <Transition appear show={isImportModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[9999]"
          onClose={closeImportModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl lg:max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                  {importResult ? (
                    <>
                      {/* ─── Result Header ─── */}
                      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50/60">
                        <div className="flex items-center gap-3">
                          <div
                            className={`size-10 rounded-xl border flex items-center justify-center shrink-0 ${
                              importResult.skippedCount > 0
                                ? "bg-amber-50 border-amber-200 text-amber-600"
                                : "bg-emerald-50 border-emerald-200 text-emerald-600"
                            }`}
                          >
                            {importResult.skippedCount > 0 ? (
                              <AlertTriangle className="size-5 text-amber-600" />
                            ) : (
                              <CheckCircle2 className="size-5 text-emerald-600" />
                            )}
                          </div>
                          <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                              Import Processing Summary
                            </Dialog.Title>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Processed {importResult.totalRows || 0} total row
                              {importResult.totalRows !== 1 ? "s" : ""} from
                              spreadsheet
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={closeImportModal}
                            className="size-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
                          >
                            <X className="size-5" />
                          </button>
                        </div>
                      </div>

                      {/* ─── Result Body ─── */}
                      <div className="p-6 space-y-5">
                        {/* Summary Metrics Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                            <div className="size-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-700 shadow-xs shrink-0">
                              <FileSpreadsheet className="size-5" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500">
                                Total Rows
                              </p>
                              <p className="text-lg font-bold text-slate-800 leading-tight mt-0.5">
                                {importResult.totalRows || 0}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-emerald-50/60 border border-emerald-200/70">
                            <div className="size-10 rounded-lg bg-white border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-xs shrink-0">
                              <CheckCircle2 className="size-5" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-emerald-700">
                                Successfully Imported
                              </p>
                              <p className="text-lg font-bold text-emerald-800 leading-tight mt-0.5">
                                {importResult.importedCount || 0}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-rose-50/60 border border-rose-200/70">
                            <div className="size-10 rounded-lg bg-white border border-rose-200 flex items-center justify-center text-rose-600 shadow-xs shrink-0">
                              <AlertCircle className="size-5" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-rose-700">
                                Skipped / Rejected
                              </p>
                              <p className="text-lg font-bold text-rose-800 leading-tight mt-0.5">
                                {importResult.skippedCount || 0}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Tabs Bar */}
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setResultTab("skipped")}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                resultTab === "skipped"
                                  ? "bg-rose-100/80 text-rose-800 shadow-xs"
                                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              <AlertCircle className="size-3.5" />
                              <span>
                                Rejected Records (
                                {importResult.skippedCount ||
                                  importResult.skipped?.length ||
                                  0}
                                )
                              </span>
                            </button>
                            {importResult.importedCount > 0 && (
                              <button
                                type="button"
                                onClick={() => setResultTab("imported")}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                  resultTab === "imported"
                                    ? "bg-emerald-100/80 text-emerald-800 shadow-xs"
                                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                <Check className="size-3.5" />
                                <span>
                                  Imported Records (
                                  {importResult.importedCount ||
                                    importResult.imported?.length ||
                                    0}
                                  )
                                </span>
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 hidden sm:block">
                            {resultTab === "skipped"
                              ? "Review the failure reasons below"
                              : "Successfully registered in database"}
                          </p>
                        </div>

                        {/* Tab Content */}
                        {resultTab === "skipped" ? (
                          <div>
                            {!importResult.skipped ||
                            importResult.skipped.length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No records were skipped.
                              </div>
                            ) : (
                              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
                                <div className="max-h-[300px] overflow-y-auto scrollbar">
                                  <table className="w-full text-left border-collapse">
                                    <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                                      <tr>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                                          Excel Row
                                        </th>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-44">
                                          Customer
                                        </th>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-56">
                                          Email
                                        </th>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                          Rejection Reason
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-xs">
                                      {importResult.skipped.map((item, idx) => (
                                        <tr
                                          key={idx}
                                          className="hover:bg-gray-50/70 transition-colors"
                                        >
                                          {/* Row Number */}
                                          <td className="py-3 px-4 whitespace-nowrap">
                                            <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-mono font-medium text-xs border border-gray-200">
                                              Row {item.row || idx + 1}
                                            </span>
                                          </td>

                                          {/* Customer Name */}
                                          <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                                            {item.customerFullName ||
                                              item.fullName ||
                                              [item.firstName, item.lastName]
                                                .filter(Boolean)
                                                .join(" ") || (
                                                <span className="text-gray-400 italic">
                                                  Not specified
                                                </span>
                                              )}
                                          </td>

                                          {/* Email */}
                                          <td className="py-3 px-4 text-gray-700 break-all">
                                            {item.email || (
                                              <span className="text-gray-400 italic">
                                                No email
                                              </span>
                                            )}
                                          </td>

                                          {/* Rejection Reason */}
                                          <td className="py-3 px-4">
                                            <div className="flex items-start gap-1.5 text-rose-700">
                                              <AlertCircle className="size-3.5 text-rose-500 shrink-0 mt-0.5" />
                                              <span className="font-medium text-xs leading-relaxed">
                                                {item.reason ||
                                                  "Validation failed"}
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            {!importResult.imported ||
                            importResult.imported.length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No records were imported.
                              </div>
                            ) : (
                              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
                                <div className="max-h-[300px] overflow-y-auto scrollbar">
                                  <table className="w-full text-left border-collapse">
                                    <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                                      <tr>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                          Customer Name
                                        </th>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                          Email
                                        </th>
                                        <th className="py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                                          Status
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-xs">
                                      {importResult.imported.map((item, idx) => (
                                        <tr
                                          key={item.id || idx}
                                          className="hover:bg-gray-50/70 transition-colors"
                                        >
                                          <td className="py-3 px-4 font-medium text-gray-900">
                                            {[item.firstName, item.lastName]
                                              .filter(Boolean)
                                              .join(" ") ||
                                              item.customerFullName ||
                                              item.fullName ||
                                              "Customer"}
                                          </td>
                                          <td className="py-3 px-4 text-gray-700">
                                            {item.email || "—"}
                                          </td>
                                          <td className="py-3 px-4 text-right">
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                              <CheckCircle2 className="size-3 text-emerald-600" />
                                              Imported
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ─── Result Footer ─── */}
                      <div className="flex items-center justify-between gap-3 px-6 py-4 bg-gray-50/80 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={handleResetImport}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-xs"
                        >
                          <RotateCcw className="size-3.5 text-gray-500" />
                          Import Another File
                        </button>
                        <button
                          type="button"
                          onClick={closeImportModal}
                          className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-[#115B9D] rounded-xl hover:opacity-90 transition-all shadow-sm"
                        >
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* ─── Upload Modal Header ─── */}
                      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-center gap-3">
                          <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/20 flex items-center justify-center shrink-0">
                            <FileSpreadsheet className="size-5 text-[#115B9D]" />
                          </div>
                          <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                              Import Customers
                            </Dialog.Title>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Upload an Excel file (.xlsx, .xls) to bulk import
                              customers
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={closeImportModal}
                          disabled={isUploading}
                          className="size-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors disabled:opacity-50"
                        >
                          <X className="size-5" />
                        </button>
                      </div>

                      {/* ─── Upload Modal Body ─── */}
                      <div className="p-6 space-y-5">
                        {/* Step 1: Download Template */}
                        <div className="bg-[#115B9D]/5 border border-[#115B9D]/15 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="size-9 rounded-lg bg-white border border-[#115B9D]/25 flex items-center justify-center shrink-0 shadow-xs mt-0.5 sm:mt-0">
                              <Download className="size-4 text-[#115B9D]" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                Need the standard Excel template?
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                Download pre-formatted template with all
                                required column headers
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleSampleDownload}
                            disabled={isDownloadingSample}
                            className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-lg text-[#115B9D] bg-white border border-[#115B9D]/30 hover:bg-[#115B9D] hover:text-white transition-all shadow-xs disabled:opacity-50"
                          >
                            <Download className="size-3.5" />
                            {isDownloadingSample
                              ? "Downloading..."
                              : "Download Sample"}
                          </button>
                        </div>

                        {/* Step 2: Upload Area */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                            Upload Excel File
                          </label>

                          {!importFile ? (
                            <div
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={handleDrop}
                              onClick={() => fileInputRef.current?.click()}
                              className={`relative border-2 border-dashed rounded-xl p-7 text-center cursor-pointer transition-all ${
                                dragOver
                                  ? "border-[#115B9D] bg-[#115B9D]/10"
                                  : "border-gray-200 hover:border-[#115B9D] hover:bg-[#115B9D]/5"
                              }`}
                            >
                              <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                onChange={handleFileChange}
                              />
                              <div className="flex flex-col items-center gap-2.5">
                                <div className="size-12 rounded-full bg-[#115B9D]/10 flex items-center justify-center text-[#115B9D]">
                                  <Upload className="size-6" />
                                </div>
                                <div className="space-y-1">
                                  <p className="text-sm font-medium text-gray-800">
                                    <span className="text-[#115B9D] font-semibold">
                                      Click to browse
                                    </span>{" "}
                                    or drag and drop file here
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Supports Microsoft Excel (.xlsx, .xls) up to
                                    10MB
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="border border-[#115B9D]/30 bg-[#115B9D]/5 rounded-xl p-4 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="size-11 rounded-xl bg-white border border-[#115B9D]/20 flex items-center justify-center shrink-0 shadow-xs">
                                  <FileSpreadsheet className="size-6 text-[#008236]" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {importFile.name}
                                  </p>
                                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                                    <span>
                                      {formatFileSize(importFile.size)}
                                    </span>
                                    <span>•</span>
                                    <span className="inline-flex items-center text-[#008236] font-medium gap-0.5">
                                      <CheckCircle2 className="size-3" /> Ready
                                      for import
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={isUploading}
                                  className="px-2.5 py-1.5 text-xs text-gray-600 hover:text-[#115B9D] hover:bg-white rounded-lg transition-colors font-medium border border-transparent hover:border-gray-200"
                                >
                                  Change
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setImportFile(null);
                                    if (fileInputRef.current)
                                      fileInputRef.current.value = "";
                                  }}
                                  disabled={isUploading}
                                  title="Remove file"
                                  className="size-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                                >
                                  <Trash2 className="size-4" />
                                </button>
                              </div>
                              <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                onChange={handleFileChange}
                              />
                            </div>
                          )}
                        </div>

                        {/* Instructions / Tips */}
                        <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/80 text-xs text-slate-600 space-y-1">
                          <p className="font-semibold text-slate-800">
                            Import Guidelines:
                          </p>
                          <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
                            <li>Customer email addresses must be unique.</li>
                            <li>
                              First Name and Last Name or Customer Full Name
                              columns are required.
                            </li>
                            <li>
                              Download the sample template to avoid formatting
                              or column header errors.
                            </li>
                          </ul>
                        </div>
                      </div>

                      {/* ─── Upload Modal Footer ─── */}
                      <div className="flex items-center justify-end gap-2.5 px-6 py-4 bg-gray-50/70 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={closeImportModal}
                          disabled={isUploading}
                          className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleImportSubmit}
                          disabled={!importFile || isUploading}
                          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-[#115B9D] rounded-xl hover:opacity-90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUploading ? (
                            <>
                              <svg
                                className="animate-spin size-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Importing...
                            </>
                          ) : (
                            <>
                              <FileSpreadsheet className="size-4" />
                              Import Customers
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
};

export default UsersListing;

